import { SelectionFeature, SelectionType } from "src/js/stores/map/store";
import { feature, point } from "@turf/turf";
import { useCallback, useMemo } from "react";

import { LayerTypeRightClickMenuProps } from "../../types";
import { LtIcons } from "@landtechnologies/icons/font/lt-icons";
import { RightClick } from "react-migration/components/RightClick";
import { TenureType } from "react-migration/domains/ownership/typings/Tenure";
import { useRegionAccess } from "react-migration/lib/util/useRegionAccess";
import { useTitlePolysByLocation } from "react-migration/domains/ownership/apollo/useTitlePolysByLocation/useTitlePolysByLocation";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

type Title = ReturnType<typeof useTitlePolysByLocation>["titlesByLocation"][0];

interface TitleItemProps {
  title: Title;
  setHoveredFeature(selection?: SelectionFeature): void;
  titleSelectionHandler(title: Title): void;
}

export function OwnershipRightClickMenu({
  rightClickLocation,
  setSelection,
  setHoveredFeature,
}: LayerTypeRightClickMenuProps) {
  const { mapCoordinate: clickedCoordinate } = rightClickLocation;
  const { t } = useTranslation();

  const { isUS } = useRegionAccess();
  const location = useMemo(() => point([clickedCoordinate[0], clickedCoordinate[1]]), [clickedCoordinate]);
  const { titlesByLocation } = useTitlePolysByLocation(location.geometry);

  const titleSelectionHandler = useCallback(
    (title: Title) => setSelection(createTitleSelection(title)),
    [setSelection]
  );

  const [firstBucket, secondBucket] = useMemo(() => {
    if (!titlesByLocation.length) return [null, null];

    return [titlesByLocation.slice(0, 4), titlesByLocation.slice(4)];
  }, [titlesByLocation]);

  if (!firstBucket?.length) return null;

  const getDisplayId = (title: Title) => (isUS ? title.parcel_identifier : title.title_no);

  const TitleItem = ({ title, setHoveredFeature, titleSelectionHandler }: TitleItemProps) => {
    const displayId = getDisplayId(title);
    if (!displayId) return null;

    return (
      <RightClick.Item
        leadIcon={getLeadIcon(isUS, title.tenure)}
        onMouseEnter={() => setHoveredFeature(createTitleSelection(title))}
        onMouseLeave={() => setHoveredFeature(undefined)}
        onClick={() => titleSelectionHandler(title)}
      >
        {displayId}
      </RightClick.Item>
    );
  };

  return (
    <RightClick.Group title={t("ownership.right_click.title")}>
      {firstBucket.map((title) => (
        <TitleItem
          key={getDisplayId(title)}
          title={title}
          setHoveredFeature={setHoveredFeature}
          titleSelectionHandler={titleSelectionHandler}
        />
      ))}

      {!!secondBucket?.length && (
        <RightClick.Sub>
          <RightClick.SubTrigger>{secondBucket.length} more</RightClick.SubTrigger>
          <RightClick.Portal>
            <RightClick.SubContent>
              {secondBucket.map((title) => (
                <TitleItem
                  key={getDisplayId(title)}
                  title={title}
                  setHoveredFeature={setHoveredFeature}
                  titleSelectionHandler={titleSelectionHandler}
                />
              ))}
            </RightClick.SubContent>
          </RightClick.Portal>
        </RightClick.Sub>
      )}
    </RightClick.Group>
  );
}

function getLeadIcon(isUS: boolean, tenure?: TenureType): `${LtIcons}` | undefined {
  if (isUS) return "lt-shape-line";

  switch (tenure) {
    case TenureType.FREEHOLD:
      return "lt-freehold";
    case TenureType.LEASEHOLD:
      return "lt-leasehold";
    default:
      return "lt-unknown-tenure";
  }
}

function createTitleSelection(title: Title): SelectionFeature | undefined {
  if (!title.title_no || !title.mergedTitlePolygon) return;

  return {
    type: SelectionType.OWNERSHIP_TITLE,
    id: title.title_no,
    feature: feature(title.mergedTitlePolygon),
  };
}
